import { api, API, getTrialId, getToken } from "@/lib/api";

const options = () => ({ params: { trial_id: getTrialId() }, timeout: 20000 });
export const assistantApi = {
  presets: () => api.get('/assistant/presets', { timeout: 20000 }).then((r) => r.data),
  createPreset: (name, brief) => api.post('/assistant/presets', { name, brief }, { timeout: 20000 }).then((r) => r.data),
  updatePreset: (id, name, brief) => api.put(`/assistant/presets/${id}`, { name, brief }, { timeout: 20000 }).then((r) => r.data),
  deletePreset: (id) => api.delete(`/assistant/presets/${id}`, { timeout: 20000 }),
  list: () => api.get("/assistant/conversations", options()).then((r) => r.data),
  create: () => api.post("/assistant/conversations", {}, options()).then((r) => r.data),
  get: (id) => api.get(`/assistant/conversations/${id}`, options()).then((r) => r.data),
  save: (id, revision, brief) => api.patch(`/assistant/conversations/${id}/brief`, { revision, brief }, options()).then((r) => r.data),
  attach: (id, revision, uploadId, duration) => api.put(`/assistant/conversations/${id}/video`, {
    revision, upload_id: uploadId, duration_seconds: duration,
  }, options()).then((r) => r.data),
  confirm: (id, revision) => api.post(`/assistant/conversations/${id}/confirm`, { revision, confirmed: true }, options()).then((r) => r.data),
};

export function assistantError(error) {
  if (error.name === "AbortError") return "The reply was interrupted. Your message is saved; retry to continue.";
  const detail = error.response?.data?.detail;
  return typeof detail === "string" ? detail : error.message || "Something went wrong. Please try again.";
}

export async function streamAssistant(id, payload, onDelta, signal) {
  const token = getToken();
  const response = await fetch(`${API}/assistant/conversations/${id}/messages?trial_id=${encodeURIComponent(getTrialId())}`, {
    method: "POST", headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload), signal,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(typeof data.detail === "string" ? data.detail : "The specialist could not connect. Please retry.");
  }
  if (!response.body) throw new Error("Streaming is unavailable in this browser. Please try again.");
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let buffer = "", result = null;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop();
      for (const frame of frames) {
        const kind = frame.split("\n").find((line) => line.startsWith("event: "))?.slice(7);
        const raw = frame.split("\n").filter((line) => line.startsWith("data: ")).map((line) => line.slice(6)).join("\n");
        if (!raw) continue;
        const data = JSON.parse(raw);
        if (kind === "delta") onDelta(data);
        if (kind === "error") throw new Error(data);
        if (kind === "result") result = data;
      }
    }
  } finally {
    reader.releaseLock();
  }
  if (!result) throw new Error("The reply was interrupted. Your conversation is saved; please retry.");
  return result;
}

export function videoDuration(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video"), url = URL.createObjectURL(file);
    let finished = false;
    const settle = (value) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      video.onloadedmetadata = video.onerror = null;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => settle(null), 12000);
    video.preload = "metadata";
    video.onloadedmetadata = () => settle(Number.isFinite(video.duration) && video.duration > 0 && video.duration <= 86400 ? video.duration : null);
    video.onerror = () => settle(null);
    video.src = url;
  });
}

export const PRESETS = [
  ["tmpl_hormozi", "Creator Classic"], ["tmpl_clean", "Clean Minimal"], ["tmpl_beast", "Kinetic Pop"],
  ["tmpl_minimal", "Cinematic Story"], ["tmpl_explainer", "Karaoke Explainer"],
];
export const sizeLabel = (bytes) => bytes < 1048576 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1048576).toFixed(1)} MB`;
